// src/pages/UserPage/sections/PaymentsSection.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, Divider, Stack, Tooltip, TextField, InputAdornment,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { FileText, CheckCircle2, Clock, Save, Hourglass } from 'lucide-react';
import { collection, getDocs, orderBy, query, writeBatch, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { generateInvoicePdf } from '../../../utils/printUtils/invoice';
import { makeFinvoiceXml } from '../../../utils/invoicing/courierXML';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('fi-FI', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatEur(n) {
  if (n == null) return '—';
  return `${Number(n).toFixed(2)} €`;
}

/** billingStatus is { status, time } — this extracts the status string safely */
function getBillingStatus(d) {
  return d.billingStatus?.status ?? 'processing';
}

const STATUS_COLORS = {
  processing: 'default',
  unpaid:     'warning',
  invoiced:   'info',
  paid:       'success',
};

const STATUS_LABELS = {
  processing: 'Käsittelyssä',
  unpaid:     'Maksamatta',
  invoiced:   'Laskutettu',
  paid:       'Maksettu',
};

const STATUS_ICON = {
  processing: <Hourglass size={12} />,
  paid:       <CheckCircle2 size={12} />,
};

// ─── Courier info form ────────────────────────────────────────────────────────

function CourierInfoForm({ userId, initial, onSaved }) {
  const [form, setForm] = useState({
    companyName: initial.companyName || '',
    yNumber:     initial.yNumber     || '',
    iban:        initial.iban        || '',
    bic:         initial.bic         || '',
    alvRate:     initial.alvRate     ?? 25.5,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'users', userId), {
        'payment.companyName': form.companyName,
        'payment.yNumber':     form.yNumber,
        'payment.iban':        form.iban,
        'payment.bic':         form.bic,
        'payment.alvRate':     Number(form.alvRate),
      });
      onSaved({ ...form, alvRate: Number(form.alvRate) });
      setSaved(true);
    } catch (err) {
      console.error('Error saving courier info:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Omat laskutustiedot</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr 80px' }, gap: 2 }}>
        <TextField
          label="Yrityksen nimi" size="small" value={form.companyName}
          onChange={handleChange('companyName')}
        />
        <TextField
          label="Y-tunnus" size="small" value={form.yNumber}
          onChange={handleChange('yNumber')}
        />
        <TextField
          label="IBAN" size="small" value={form.iban}
          onChange={handleChange('iban')}
        />
        <TextField
          label="BIC" size="small" value={form.bic}
          onChange={handleChange('bic')}
        />
        <TextField
          label="ALV %" size="small" type="number" value={form.alvRate}
          onChange={handleChange('alvRate')}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
        />
      </Box>
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Button
          size="small" variant="contained"
          onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={14} />}
        >
          {saving ? 'Tallennetaan...' : 'Tallenna'}
        </Button>
        {saved && (
          <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircle2 size={13} /> Tallennettu
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function PaymentsSection({ user, userData }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [selected, setSelected]     = useState(new Set());
  const [invoicing, setInvoicing]   = useState(false);
  const [invoiceFormat, setInvoiceFormat] = useState('pdf'); // 'pdf' | 'xml'

  const [courierInfo, setCourierInfo] = useState({
    companyName: userData?.payment?.companyName || '',
    yNumber:     userData?.payment?.yNumber     || '',
    iban:        userData?.payment?.iban        || '',
    bic:         userData?.payment?.bic         || '',
    alvRate:     userData?.payment?.alvRate     ?? 25.5,
  });

  // ─── Fetch ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, 'payments', user.uid, 'deliveries'),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        setDeliveries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error fetching payments:', err);
        setError('Virhe ladattaessa maksutietoja.');
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.uid]);

  // ─── Selection ──────────────────────────────────────────────────────────


  // ─── Week helpers ─────────────────────────────────────────────────────────────

/** Returns the Monday 00:00:00 of the week containing `ms` */
function getWeekStart(ms) {
  const d = new Date(ms);
  const day = d.getDay(); // 0 = Sun, 1 = Mon, ...
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// ─── Selection ────────────────────────────────────────────────────────────────

const isSelectable = (d) => {
  const now              = Date.now();
  const isSunday         = new Date().getDay() === 0;
  const thisWeekStart    = getWeekStart(now);
  const lastWeekStart    = thisWeekStart - 7 * 24 * 60 * 60 * 1000;

  const doneThisWeek        = d.createdAt >= thisWeekStart;
  const doneLastWeekOrLater = d.createdAt >= lastWeekStart;
  const notPaid             = getBillingStatus(d) !== 'paid';

  // Sunday: can invoice anything completed this week
  // Any day: can invoice unpaid deliveries from last week or later
  return (isSunday && doneThisWeek) || (notPaid && doneLastWeekOrLater);
};

  const selectableDeliveries = useMemo(
    () => deliveries.filter(isSelectable),
    [deliveries]
  );

  const toggleSelect = (id) => {
    const delivery = deliveries.find((d) => d.id === id);
    if (!delivery || !isSelectable(delivery)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () =>
    setSelected(
      selected.size === selectableDeliveries.length && selectableDeliveries.length > 0
        ? new Set()
        : new Set(selectableDeliveries.map((d) => d.id))
    );

  // ─── Derived ────────────────────────────────────────────────────────────

  const selectedDeliveries = useMemo(
    () => deliveries.filter((d) => selected.has(d.id)),
    [deliveries, selected]
  );

  const groupedByYNumber = useMemo(() => {
    const groups = {};
    for (const d of selectedDeliveries) {
      const key = d.billing?.yNumber || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          yNumber:     d.billing?.yNumber     || '—',
          companyName: d.billing?.companyName || '—',
          deliveries:  [],
        };
      }
      groups[key].deliveries.push(d);
    }
    return Object.values(groups);
  }, [selectedDeliveries]);

  const selectedNet   = useMemo(
    () => selectedDeliveries.reduce((s, d) => s + (Number(d.reward) || 0), 0),
    [selectedDeliveries]
  );
  const selectedGross = useMemo(
    () => selectedNet * (1 + courierInfo.alvRate / 100),
    [selectedNet, courierInfo.alvRate]
  );

  // ─── Generate invoices ────────────────────────────────────────────────

  const handleGenerateInvoices = async () => {
    if (!selectedDeliveries.length) return;
    try {
      setInvoicing(true);

      if (invoiceFormat === 'xml') {
        // reward is net → convert to gross so makeFinvoiceXml can back-calculate net internally
        const mapped = selectedDeliveries.map((d) => ({
          ...d,
          deliveryId:      d.deliveryId || d.id,
          price:           (Number(d.reward) || 0) * (1 + courierInfo.alvRate / 100),
          originInfo:      { address: d.origin      || '' },
          destinationInfo: { address: d.destination || '' },
          targetTime:      d.createdAt || d.createdAtMs   || Date.now(),
        }));

        const seller = {
          name:    courierInfo.companyName,
          yNumber: courierInfo.yNumber,
          taxCode: 'FI' + (courierInfo.yNumber || '').replace('-', ''),
          iban:    courierInfo.iban,
          bic:     courierInfo.bic,
        };

        const xmlFiles = makeFinvoiceXml(mapped, { seller, noBillingFee: true });

        for (const { yNumber, xml } of xmlFiles) {
          const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href     = url;
          a.download = `finvoice-${yNumber}.xml`;
          a.click();
          URL.revokeObjectURL(url);
        }
      } else {
        for (const group of groupedByYNumber) {
          const invoiceNumber = `${Date.now()}-${group.yNumber.replace(/[^a-zA-Z0-9]/g, '')}`;
          generateInvoicePdf({
            courierInfo,
            clientCompany: group.companyName,
            clientYNumber: group.yNumber,
            deliveries:    group.deliveries,
            invoiceNumber,
            alvRate:       courierInfo.alvRate,
          });
        }
      }

      // Mark all selected as invoiced regardless of format
      const invoicedStatus = { status: 'invoiced', time: Date.now() };
      const batch = writeBatch(db);
      for (const d of selectedDeliveries) {
        batch.update(
          doc(db, 'payments', user.uid, 'deliveries', d.id),
          { billingStatus: invoicedStatus }
        );
      }
      await batch.commit();

      setDeliveries((prev) =>
        prev.map((d) => selected.has(d.id) ? { ...d, billingStatus: invoicedStatus } : d)
      );
      setSelected(new Set());
    } catch (err) {
      console.error('Error generating invoices:', err);
      setError('Virhe laskujen luomisessa.');
    } finally {
      setInvoicing(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Ladataan...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <CourierInfoForm userId={user.uid} initial={courierInfo} onSaved={setCourierInfo} />

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            {deliveries.length} toimitusta
          </Typography>

          {selected.size > 0 && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {selected.size} valittu · netto {formatEur(selectedNet)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  sis. ALV {courierInfo.alvRate}% · {formatEur(selectedGross)}
                </Typography>
              </Box>
              {groupedByYNumber.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  ({groupedByYNumber.length} laskua)
                </Typography>
              )}
              <ToggleButtonGroup
                size="small"
                value={invoiceFormat}
                exclusive
                onChange={(_, v) => v && setInvoiceFormat(v)}
              >
                <ToggleButton value="pdf" sx={{ px: 1.5, fontSize: '0.7rem' }}>PDF</ToggleButton>
                <ToggleButton value="xml" sx={{ px: 1.5, fontSize: '0.7rem' }}>XML</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained" size="small"
                onClick={handleGenerateInvoices} disabled={invoicing}
                startIcon={invoicing ? <CircularProgress size={14} color="inherit" /> : <FileText size={15} />}
              >
                {invoicing ? 'Luodaan...' : `Luo lasku${groupedByYNumber.length > 1 ? 't' : ''}`}
              </Button>
            </Stack>
          )}
        </Box>

        {deliveries.length === 0 ? (
          <Alert severity="info">Ei vielä maksutietoja.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectableDeliveries.length > 0 && selected.size === selectableDeliveries.length}
                      indeterminate={selected.size > 0 && selected.size < selectableDeliveries.length}
                      disabled={selectableDeliveries.length === 0}
                      onChange={toggleAll}
                    />
                  </TableCell>
                  {['Toimitusnro', 'Pvm', 'Reitti', 'Yritys', 'Netto', 'Tila'].map((h) => (
                    <TableCell key={h}>
                      <Typography variant="caption" fontWeight={700}>{h}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveries.map((d) => {
                  const status     = getBillingStatus(d);
                  const selectable = isSelectable(d);
                  return (
                    <TableRow
                      key={d.id}
                      hover={selectable}
                      selected={selected.has(d.id)}
                      onClick={() => selectable && toggleSelect(d.id)}
                      sx={{
                        cursor:  selectable ? 'pointer' : 'default',
                        opacity: selectable ? 1 : 0.5,
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.has(d.id)}
                          disabled={!selectable}
                          onChange={() => toggleSelect(d.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">{d.deliveryId || d.id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{formatDate(d.createdAt 

                          || d.createdAtMs
                        )}</Typography>
                      </TableCell>

                      <TableCell>
                        <Tooltip title={d.destination || ''}>
                        <Typography variant="caption" sx={{ maxWidth: 110, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.origin || '—'}
                          </Typography>
                          <Typography variant="caption" sx={{ maxWidth: 110, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.destination || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{d.billing?.companyName || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={600}>{formatEur(d.reward)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[status] || status}
                          color={STATUS_COLORS[status]  || 'default'}
                          size="small"
                          icon={STATUS_ICON[status] ?? <Clock size={12} />}
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Summary footer */}
        {deliveries.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
              {['processing', 'unpaid', 'invoiced', 'paid'].map((status) => {
                const net = deliveries
                  .filter((d) => getBillingStatus(d) === status)
                  .reduce((sum, d) => sum + (Number(d.reward) || 0), 0);
                if (!net) return null;
                const gross = net * (1 + courierInfo.alvRate / 100);
                return (
                  <Box key={status} sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">{STATUS_LABELS[status]}</Typography>
                    <Typography variant="body2" fontWeight={700}>{formatEur(gross)}</Typography>
                    <Typography variant="caption" color="text.secondary">netto {formatEur(net)}</Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}